import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';

interface Assignment {
  assignment_id: string;
  institute_id: string;
  branch_id: string | null;
  batch_id: string;
  assignment_title: string;
  notes: string;
  start_date: string | null;
  start_time: string | null;
  submission_date: string | null;
  submission_time: string | null;
  allow_late_submissions: boolean;
  files: any[];
  created_at: string;
  created_by: string;
}

interface Batch {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  course_id: string;
  batch_name: string;
  batch_time: string;
  is_active: boolean;
}

interface AddAssignmentModalProps {
  visible: boolean;
  instituteId: string;
  branchId: string;
  editingAssignment?: Assignment | null;
  onHide: () => void;
  onSuccess?: () => void;
}

interface UploadedFile { file: File; name: string; size: number; }

// ── Design tokens — same palette as AddNoticeModal ─────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const getFileIcon = (name: string) => {
  const ext = name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  if (ext === 'pdf')                return 'pi pi-file-pdf';
  if (['doc','docx'].includes(ext)) return 'pi pi-file-word';
  if (['xls','xlsx'].includes(ext)) return 'pi pi-file-excel';
  if (['jpg','jpeg'].includes(ext)) return 'pi pi-image';
  return 'pi pi-file';
};

const getFileExt    = (name: string) => name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
const formatFileSize = (b: number)   => { if (!b) return '0 B'; const k=1024,s=['B','KB','MB']; const i=Math.floor(Math.log(b)/Math.log(k)); return Math.round((b/Math.pow(k,i))*100)/100+' '+s[i]; };

// Split an ISO-ish datetime/date string into separate date + time inputs.
const splitDate = (v: string | null) => (v ? v.slice(0, 10) : '');
const splitTime = (v: string | null) => (v ? v.slice(0, 5) : '');

const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({
  visible, instituteId, branchId, editingAssignment, onHide, onSuccess,
}) => {
  const toast        = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [batches,        setBatches]        = useState<Batch[]>([]);
  const [loadingBatches, setLoadingBatches]  = useState(false);
  const [uploadedFiles,  setUploadedFiles]  = useState<UploadedFile[]>([]);
  const [errors,         setErrors]         = useState<Record<string, string>>({});

  const isEditing = !!editingAssignment;

  const [formData, setFormData] = useState({
    batch_id:               '',
    assignment_title:       '',
    notes:                  '',
    start_date:             '',
    start_time:             '',
    submission_date:        '',
    submission_time:        '',
    allow_late_submissions: false,
  });

  const MAX_FILES       = 3;
  const MAX_TOTAL_SIZE  = 6 * 1024 * 1024;
  const ALLOWED_FORMATS = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/jpg'
  ];
  const ALLOWED_EXTENSIONS = ['pdf','xls','xlsx','doc','docx','jpeg','jpg'];

  const showToast = (severity: 'success'|'error'|'warn'|'info', summary: string, detail: string, life = 3500) =>
    toast.current?.show({ severity, summary, detail, life });

  // Batches: only fetched for create mode — editing can't change batch_id
  // (backend's updateAssignmentController doesn't accept it).
  useEffect(() => {
    if (visible && !isEditing && batches.length === 0) fetchBatches();
  }, [visible, isEditing]);

  useEffect(() => {
    if (editingAssignment) {
      setFormData({
        batch_id:               editingAssignment.batch_id,
        assignment_title:       editingAssignment.assignment_title,
        notes:                  editingAssignment.notes || '',
        start_date:             splitDate(editingAssignment.start_date),
        start_time:             splitTime(editingAssignment.start_time),
        submission_date:        splitDate(editingAssignment.submission_date),
        submission_time:        splitTime(editingAssignment.submission_time),
        allow_late_submissions: !!editingAssignment.allow_late_submissions,
      });
      setUploadedFiles([]);
    } else {
      setFormData({
        batch_id: '', assignment_title: '', notes: '',
        start_date: '', start_time: '', submission_date: '', submission_time: '',
        allow_late_submissions: false,
      });
      setUploadedFiles([]);
    }
    setErrors({});
    setError(null);
  }, [editingAssignment, visible]);

  const fetchBatches = async () => {
    try {
      setLoadingBatches(true);
      const res = await apiClient.get(`/v1/batches?page=1&limit=100`);
      if (res.data.success) setBatches((res.data.data || []).filter((b: Batch) => b.is_active));
      else showToast('error', 'Error', 'Failed to load batches');
    } catch (err: any) {
      showToast('error', 'Error', err.response?.data?.error || 'Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const batchOptions: DropdownOption[] = batches.map(b => ({
    label: `${b.batch_name} (${b.batch_time || 'N/A'})`,
    value: b.batch_id,
  }));

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const getTotalSize = () => uploadedFiles.reduce((t, f) => t + f.size, 0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    if (uploadedFiles.length + files.length > MAX_FILES) {
      showToast('error', 'Limit', `Max ${MAX_FILES} files allowed`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles: UploadedFile[] = [];
    const fileErrors: string[]     = [];
    Array.from(files).forEach(file => {
      const ext = getFileExt(file.name);
      if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: Invalid format`);
        return;
      }
      newFiles.push({ file, name: file.name, size: file.size });
    });
    if (getTotalSize() + newFiles.reduce((s, f) => s + f.size, 0) > MAX_TOTAL_SIZE) {
      showToast('error', 'Size Limit', `Total cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (fileErrors.length) showToast('error', 'Invalid Files', fileErrors.join(', '));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index: number) => setUploadedFiles(prev => prev.filter((_, i) => i !== index));

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!isEditing && !formData.batch_id) e.batch_id = 'Batch is required';
    if (!formData.assignment_title.trim()) e.assignment_title = 'Title is required';
    if (!formData.notes.trim()) e.notes = 'Notes / instructions are required';

    if (formData.submission_date && formData.start_date) {
      const start = new Date(`${formData.start_date}T${formData.start_time || '00:00'}`);
      const submission = new Date(`${formData.submission_date}T${formData.submission_time || '00:00'}`);
      if (submission <= start) e.submission_date = 'Submission must be after start date/time';
    }

    setErrors(e);
    if (Object.keys(e).length) showToast('error', 'Validation Failed', 'Please correct all highlighted fields');
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);

    const payload = new FormData();

    if (!isEditing) {
      payload.append('institute_id', instituteId);
      payload.append('branch_id',    branchId);
      payload.append('batch_id',     formData.batch_id);
    }

    payload.append('assignment_title', formData.assignment_title.trim());
    payload.append('notes',            formData.notes.trim());
    payload.append('allow_late_submissions', formData.allow_late_submissions ? 'true' : 'false');

    if (formData.start_date)      payload.append('start_date', formData.start_date);
    if (formData.start_time)      payload.append('start_time', formData.start_time);
    if (formData.submission_date) payload.append('submission_date', formData.submission_date);
    if (formData.submission_time) payload.append('submission_time', formData.submission_time);

    if (!isEditing) {
      uploadedFiles.forEach(f => payload.append('files[]', f.file));
    }

    try {
      const response = isEditing
        ? await apiClient.put(`/v1/assignments/${editingAssignment!.assignment_id}`, payload)
        : await apiClient.post('/v1/assignments', payload);

      if (response.data.success) {
        showToast(
          'success',
          isEditing ? 'Assignment Updated' : 'Assignment Created',
          isEditing ? 'Assignment updated successfully.' : 'Assignment published successfully.'
        );
        onSuccess?.();
        setTimeout(() => onHide(), 900);
      } else {
        showToast('error', 'Error', response.data.message || 'Operation failed');
      }
    } catch (err: any) {
      showToast('error', 'Error',
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to save assignment.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      batch_id: '', assignment_title: '', notes: '',
      start_date: '', start_time: '', submission_date: '', submission_time: '',
      allow_late_submissions: false,
    });
    setUploadedFiles([]);
    setErrors({});
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  const totalSize     = getTotalSize();
  const canUploadMore  = uploadedFiles.length < MAX_FILES && totalSize < MAX_TOTAL_SIZE;

  const dateTimeInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    border: `1px solid ${C.border}`,
    borderRadius: '7px',
    fontSize: '0.82rem',
    color: C.navy,
    background: C.white,
    outline: 'none',
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '22px' } }
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '12px', padding: '21px', marginBottom: '18px',
          color: C.white, position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-38px', right: '-38px', width: '135px', height: '135px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '90px', height: '90px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '15px', marginBottom: '9px', fontSize: '0.51rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-file-edit" style={{ fontSize: '0.53rem' }} />
              Assignment Management
            </div>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '1.13rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
              {isEditing ? 'Edit Assignment' : 'Create New Assignment'}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.64rem' }}>
              {isEditing ? 'Update assignment details below' : 'Publish a new assignment for a batch'}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '18px', fontSize: '0.81rem' }} />}

        {/* ── Form ────────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderRadius: '14px', padding: '22px', marginBottom: '18px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

            {/* Batch */}
            <SectionBox title="Batch" icon="pi-th-large">
              <GlobalDropdownField
                label="Select Batch" required value={formData.batch_id}
                onChange={val => handleInputChange('batch_id', val)}
                options={batchOptions}
                placeholder={loadingBatches ? 'Loading...' : 'Select batch'}
                error={errors.batch_id}
                disabled={loadingBatches || isEditing}
              />
              {isEditing && (
                <p style={{ margin: '9px 0 0 0', fontSize: '0.68rem', color: C.muted }}>
                  Batch can't be changed after an assignment is created.
                </p>
              )}
            </SectionBox>

            {/* Title */}
            <GlobalInputField
              label="Assignment Title" required value={formData.assignment_title}
              onChange={e => handleInputChange('assignment_title', e.target.value)}
              placeholder="e.g., Chapter 5 Problem Set"
              error={errors.assignment_title}
            />

            {/* Dates & Times */}
            <SectionBox title="Schedule" icon="pi-calendar">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.77rem', fontWeight: '600', color: C.navy }}>Start Date</label>
                  <input type="date" value={formData.start_date} onChange={e => handleInputChange('start_date', e.target.value)} style={dateTimeInputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.77rem', fontWeight: '600', color: C.navy }}>Start Time</label>
                  <input type="time" value={formData.start_time} onChange={e => handleInputChange('start_time', e.target.value)} style={dateTimeInputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.77rem', fontWeight: '600', color: C.navy }}>Submission Date</label>
                  <input type="date" value={formData.submission_date} onChange={e => handleInputChange('submission_date', e.target.value)} style={{ ...dateTimeInputStyle, borderColor: errors.submission_date ? '#dc2626' : C.border }} />
                  {errors.submission_date && <small style={{ color: '#dc2626', fontSize: '0.68rem' }}>{errors.submission_date}</small>}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.77rem', fontWeight: '600', color: C.navy }}>Submission Time</label>
                  <input type="time" value={formData.submission_time} onChange={e => handleInputChange('submission_time', e.target.value)} style={dateTimeInputStyle} />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '9px', marginTop: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.allow_late_submissions}
                  onChange={e => handleInputChange('allow_late_submissions', e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: C.navy, cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.79rem', fontWeight: '600', color: C.navy }}>Allow Late Submissions</span>
              </label>
            </SectionBox>

            {/* Notes */}
            <GlobalTextArea
              label="Notes / Instructions" required value={formData.notes}
              onChange={e => handleInputChange('notes', e.target.value)}
              placeholder="Write detailed instructions, evaluation criteria, or additional information..."
              error={errors.notes} rows={6}
            />

            {/* Attachments — create only, per updateAssignmentController not accepting files */}
            {!isEditing && (
              <SectionBox
                title="Attachments"
                icon="pi-paperclip"
                badge={`${uploadedFiles.length}/${MAX_FILES} · ${formatFileSize(totalSize)} / ${formatFileSize(MAX_TOTAL_SIZE)}`}
              >
                <input
                  ref={fileInputRef} type="file" multiple
                  accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg"
                  onChange={handleFileSelect} style={{ display: 'none' }}
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canUploadMore}
                  style={{
                    width: '100%', padding: '16px',
                    border: `2px dashed ${canUploadMore ? C.goldBorder : C.border}`,
                    borderRadius: '9px',
                    background: canUploadMore ? 'rgba(251,178,31,0.04)' : C.bg,
                    color: canUploadMore ? C.navy : C.muted,
                    cursor: canUploadMore ? 'pointer' : 'not-allowed',
                    fontSize: '0.79rem', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.08)'; e.currentTarget.style.borderColor = C.gold; } }}
                  onMouseLeave={e => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.04)'; e.currentTarget.style.borderColor = C.goldBorder; } }}
                >
                  <i className="pi pi-upload" style={{ fontSize: '0.81rem', color: canUploadMore ? C.gold : C.muted }} />
                  {canUploadMore ? 'Choose files (max 6MB total)' : 'Upload limit reached'}
                </button>

                <p style={{ margin: '7px 0 0 0', fontSize: '0.65rem', color: C.muted, textAlign: 'center' }}>
                  Allowed: PDF, Word, Excel, JPEG
                </p>

                {uploadedFiles.length > 0 && (
                  <div style={{ marginTop: '13px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    {uploadedFiles.map((fileObj, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flex: 1 }}>
                          <div style={{ width: '32px', height: '32px', background: C.bg, borderRadius: '7px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1rem', color: C.navy }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 3px 0', fontSize: '0.77rem', fontWeight: '700', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileObj.name}</p>
                            <p style={{ margin: 0, fontSize: '0.65rem', color: C.muted, fontWeight: '600' }}>{formatFileSize(fileObj.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          style={{ padding: '5px 11px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '700', transition: 'all 0.2s ease', flexShrink: 0 }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </SectionBox>
            )}
          </div>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '11px', justifyContent: 'flex-end' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={handleClose} disabled={loading} />
          <GlobalButton
            label={loading
              ? (isEditing ? 'Updating...' : 'Publishing...')
              : (isEditing ? 'Update Assignment' : 'Publish Assignment')}
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

const SectionBox: React.FC<{ title: string; icon: string; badge?: string; children: React.ReactNode }> = ({ title, icon, badge, children }) => (
  <div style={{ background: '#F5F7FA', borderRadius: '13px', padding: '16px', border: '1px solid #E5E7EB' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <div style={{ width: '29px', height: '29px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '0.77rem' }} />
        </div>
        <span style={{ fontSize: '0.79rem', fontWeight: '700', color: '#0e225d' }}>{title}</span>
      </div>
      {badge && <span style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

export default AddAssignmentModal;